import { AuthServiceLifecycleHooks, User } from '@vis-auth/vis-auth';
import { deleteFakeAuthToken } from '@vis-auth/devtools';
import { hasUserRole, SSA_USER } from '../../helpers/user-roles';
import isLocal from '../isLocal';
import { isToggleEnabled, ToggleName } from '../../api/toggles/togglesApi';

export const SSA_ROLE_PARAMETER = 'ssaRole';
export const IS_SSA = 'isSSA';
export const IS_LOGIN_DOT_GOV = 'isLoginDotGov';
export const requestPathPrefix = '/api';

export const sessionTimers = {
  // these are in milliseconds
  // Warning is time UNTIL warning modal shows up on the page.
  warning: 10 * 60 * 1000,
  // Timeout is how long user has in modal to respond.
  timeout: 5 * 60 * 1000,
  throttle: 30 * 1000,
};

export const legacySessionConfig = {
  sessionLogoutPath: 'oidc/logout',
  sessionLoginPath: 'oidc/session',
  sessionUpdatePath: 'SessionUpdater.aspx',
};

// These AuthServiceLifecycleHooks need to be run in deployed environments AND locally to support the application.
export const commonLifecycleHooks: AuthServiceLifecycleHooks = {
  onLogout: async (authToken: string, user: User) => {
    window.localStorage.removeItem(IS_LOGIN_DOT_GOV);
    window.localStorage.removeItem(IS_SSA);
    window.localStorage.removeItem(SSA_ROLE_PARAMETER);
    return Promise.all([Promise.resolve(deleteFakeAuthToken())]).then(() => Promise.resolve());
  },
  onSessionUpdate: async (authToken: string, user: User) => {
    return Promise.all([]).then(() => Promise.resolve());
  },
  onTokenReceived: async (authToken: string, user: User) => {
    return Promise.all([]).then(() => Promise.resolve());
  },
};

// These AuthServiceLifecycleHooks only need to be run in deployed environments, not locally.
const combinedLifecylceHooks: AuthServiceLifecycleHooks = {
  onLogout: async (authToken: string, user: User) => {
    if (hasUserRole(user.roles, SSA_USER)) {
      window.location.href = `${window.location.origin}/save/app/client/ui/home`;
    }
    return Promise.all([commonLifecycleHooks.onLogout(authToken)]).then(() => Promise.resolve());
  },
  onSessionUpdate: async (authToken: string) => {
    return Promise.all([commonLifecycleHooks.onSessionUpdate(authToken)]).then(() => Promise.resolve());
  },
  onTokenReceived: async (authToken: string, user: User) => {
    return Promise.all([commonLifecycleHooks.onTokenReceived(authToken, user).then(() => Promise.resolve())]).then(() =>
      Promise.resolve(),
    );
  },
};

export const getAuthLifecylceHooks = (): AuthServiceLifecycleHooks => {
  return isLocal() ? commonLifecycleHooks : combinedLifecylceHooks;
};

export enum ClientIds {
  SAVELoginDotGovDevtest = 'urn:gov:gsa:openidconnect.profiles:sp:sso:uscis:save.devtest',
  SAVEDevtest = 'SAVE-devtest',
  SAVELoginDotGovSystest = 'urn:gov:gsa:openidconnect.profiles:sp:sso:uscis:save.systest',
  SAVESystest = 'SAVE-systest',
  SAVELoginDotGovPreview = 'urn:gov:gsa:openidconnect.profiles:sp:sso:uscis:save.preview',
  SAVEPreview = 'SAVE-preview',
  SAVELoginDotGovExternaltest = 'urn:gov:gsa:openidconnect.profiles:sp:sso:uscis:save.externaltest',
  SAVEExternaltest = 'SAVE-externaltest',
  SAVELoginDotGovProd = 'urn:gov:gsa:openidconnect.profiles:sp:sso:uscis:save.prod',
  SAVEProd = 'SAVE-prod',
  SAVESsaSimulator = 'MfzzgwmqJY55xoOwPGI3',
  SAVESsaExternalTest = 'ps90RAfw3j4Kw8F15',
  SAVESsaProd = 'suAK4hw87HfU2dFs',
}

export enum Scope {
  SSASimulator = 'openid+profile+vis_scope',
  SAVE = 'vis',
  SSA = 'openid SAVE-DHS-PROFILE',
  LoginDotGov = 'openid email',
}

export const getClientId = (domain: string = window.location.origin): string => {
  switch (domain) {
    case 'https://localhost:8100':
    case 'https://legacy-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVEDevtest;
    case 'https://nonprod-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVESystest;
    case 'https://perf.save.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVEPreview;
    case 'https://stage.save.uscis.gov':
      return ClientIds.SAVEExternaltest;
    case 'https://save.uscis.gov':
      return ClientIds.SAVEProd;
    default:
      return '';
  }
};

export const getLoginDotGovClientId = (domain: string = window.location.origin): string => {
  switch (domain) {
    case 'https://localhost:8100':
    case 'https://legacy-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVELoginDotGovDevtest;
    case 'https://nonprod-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVELoginDotGovSystest;
    case 'https://perf.save.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVELoginDotGovPreview;
    case 'https://stage.save.uscis.gov':
      return ClientIds.SAVELoginDotGovExternaltest;
    case 'https://save.uscis.gov':
      return ClientIds.SAVELoginDotGovProd;
    default:
      return '';
  }
};

export const getSsaClientId = (ssaRole: string | null, domain: string = window.location.origin): string => {
  switch (domain) {
    case 'https://localhost:8100':
    case 'https://legacy-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVESsaSimulator;
    case 'https://nonprod-save-ui.vis.nonprod.uscis.dhs.gov':
      return ClientIds.SAVESsaSimulator;
    case 'https://perf.save.vis.nonprod.uscis.dhs.gov':
      return '';
    case 'https://stage.save.uscis.gov':
      return ssaRole ? ClientIds.SAVESsaExternalTest : ClientIds.SAVESsaSimulator;
    case 'https://save.uscis.gov':
      return ClientIds.SAVESsaProd;
    default:
      return '';
  }
};

export const getSsaScope = (ssaRole: string | null, domain: string = window.location.origin): string => {
  switch (domain) {
    case 'https://localhost:8100':
    case 'https://legacy-save-ui.vis.nonprod.uscis.dhs.gov':
      return Scope.SSASimulator;
    case 'https://nonprod-save-ui.vis.nonprod.uscis.dhs.gov':
      return Scope.SSASimulator;
    case 'https://perf.save.vis.nonprod.uscis.dhs.gov':
      return '';
    case 'https://stage.save.uscis.gov':
      return ssaRole ? Scope.SSA : Scope.SSASimulator;
    case 'https://save.uscis.gov':
      return Scope.SSA;
    default:
      return '';
  }
};

export const getSsaRole = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const role = urlParams.get('Role');
  if (role) {
    localStorage.setItem(SSA_ROLE_PARAMETER, role);
  }
  return role || localStorage.getItem(SSA_ROLE_PARAMETER);
};

export const isLoginDotGovLogin = (): boolean =>
  isToggleEnabled(ToggleName.EnableLoginDotGov) && onLoginDotGovLoginPage();

export const onLoginDotGovLoginPage = (): boolean => window.location.pathname.includes('loginDotGov');

export const isSSALogin = (): boolean => localStorage.getItem(IS_SSA) === 'true' || onSSALoginPage();

export const onSSALoginPage = (): boolean => window.location.pathname.includes('ssaLogin');
